import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Youtube, 
  Linkedin, 
  Globe, 
  Mail, 
  ArrowRight,
  Heart
} from 'lucide-react';
import { getAllRegions } from '../data/regions';
import { pseoAPI, newsletterAPI } from '../utils/api';
import { toast } from 'sonner';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const regions = getAllRegions();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  
  // Current active region (Global)
  const activeRegion = regions.find(r => r.code === 'global');
  // Future regions (disabled)
  const futureRegions = regions.filter(r => r.code !== 'global');

  // Fetch brands from database
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await pseoAPI.getBrands();
        setBrands(response.data || []);
      } catch (error) {
        console.error('Failed to fetch brands for footer:', error);
        setBrands([]);
      }
    };
    fetchBrands();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await newsletterAPI.subscribe({ email });
      toast.success('Subscribed! Check your inbox.');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <footer className="bg-gray-900 text-white" data-testid="footer">
      {/* Main Footer Content */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <span 
                className="text-3xl font-semibold"
                style={{
                  fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                  background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #A855F7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.02em'
                }}
              >
                iShopGenie
              </span>
            </Link>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-sm">
              Your trusted source for the latest deals, exclusive coupons, and shopping guides. Save money on everything you buy.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-2">
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-brand-red flex items-center justify-center smooth-transition" data-testid="social-facebook" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-brand-red flex items-center justify-center smooth-transition" data-testid="social-twitter" aria-label="Twitter">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-brand-red flex items-center justify-center smooth-transition" data-testid="social-instagram" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-brand-red flex items-center justify-center smooth-transition" data-testid="social-youtube" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-brand-red flex items-center justify-center smooth-transition" data-testid="social-linkedin" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-8">
            {/* Mini Newsletter */}
            <div className="bg-gray-800 rounded-2xl p-6 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-brand-yellow" />
                <h4 className="font-inter font-bold text-lg">Get Deal Alerts</h4>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Subscribe for exclusive deals delivered weekly.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="flex-1 bg-gray-700 text-white px-4 py-2.5 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  data-testid="footer-newsletter-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-brand-red hover:bg-red-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm smooth-transition disabled:opacity-50"
                  data-testid="footer-newsletter-submit"
                >
                  {loading ? '...' : <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm flex items-center gap-1">
              © {currentYear} iShopGenie. Made with <Heart className="w-4 h-4 text-brand-red inline" /> for savvy shoppers.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="text-gray-400 hover:text-white smooth-transition">About</Link>
              <Link to="/contact" className="text-gray-400 hover:text-white smooth-transition">Contact</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white smooth-transition">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white smooth-transition">Terms</Link>
              <a href="/api/sitemap.xml" className="text-gray-400 hover:text-white smooth-transition">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
